package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initAScreenEditorForBackendDrivenUiPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/AScreenEditorForBackendDrivenUi.md", FrontMatterElement.Builder { addScalar("title", "A screen editor for backend-driven UI"); addScalar("searchTitle", "kompot studio: a desktop editor for server-driven UI screens in Kotlin Multiplatform"); addScalar("date", "2026-09-03"); addScalar("description", "kompot-studio opens a screen body beside the renderers that will draw it: a tree, a JSON editor, a schema-driven inspector, a preview in the consumer's own brand frame, and findings from schema, rules, vocabulary and the render itself."); addScalar("library", "kompot"); }))
}

@Page("/blog/a-screen-editor-for-backend-driven-ui")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun AScreenEditorForBackendDrivenUiPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kompot/tree/main/kompot-studio") {
            org.jetbrains.compose.web.dom.Text("kompot-studio")
        }
        org.jetbrains.compose.web.dom.Text(" is a desktop editor for the screens a backend-driven UI server sends. The server side of ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kompot") {
            org.jetbrains.compose.web.dom.Text("kompot")
        }
        org.jetbrains.compose.web.dom.Text(" writes a tree of components blind: the JSON leaves as text, and the first time anybody sees it drawn is on a phone. The studio puts the text and the drawing side by side — the same renderers, the same brand frame, the same design system the client ships — and says what is wrong with the body before a client ever receives it.")
    }
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.graphics.Image("https://s3.kotlin.website/public/kompot-studio/konekt.png", "kompot studio open on a consumer's build: the screen list and structure on the left, the JSON with the inspector in the middle, the preview in the brand frame on the right")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-it-exists") }) {
        org.jetbrains.compose.web.dom.Text("Why it exists")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A backend-driven screen has two readers with different opinions: a JSON Schema that says which shapes are allowed, and a client that says which of them it can draw. Neither is enough alone. The schema passes a body that names a component this build does not ship, and the client draws a placeholder for it without complaint — that is the protocol working as designed, and it is exactly the thing somebody wants to see before shipping the screen. A validator answers the first question; a running app answers the second, one deployment later. The studio answers both at once, against the build that will do the drawing.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-it-does") }) {
        org.jetbrains.compose.web.dom.Text("What it does")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Opens a body from a file, a directory of recordings, or an HTTP source polled by ETag.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Draws it inside the consumer's frame: brand, light or dark, a device size, a form state.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Checks it in layers — syntax, the build's profile schema, the rules the schema cannot express, the vocabulary of words and design tokens this build answers for, and what the render reported about itself — and lists the findings with the layer that raised each.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Edits the body: drag a type from the palette into a slot, move a node by mouse or by button, change a property in an inspector generated from the schema, pick a design token from the build's dictionary.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Captures the frame and compares it with a golden through ")
            com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
                org.jetbrains.compose.web.dom.Text("viddik")
            }
            org.jetbrains.compose.web.dom.Text(", when viddik is on the classpath.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Prints a draft of the server side as Kotlin DSL.")
        }
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("install") }) {
        org.jetbrains.compose.web.dom.Text("Install")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The studio is a library the consumer's build launches: the renderers, the brand frame and the recorded screens live there. A Gradle plugin adds the task, and a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("KompotStudioConfigProvider")
        }
        org.jetbrains.compose.web.dom.Text(" in the source set says what to open it with.")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// settings.gradle.kts — Jewel's icons live only in the IntelliJ repository
maven("https://reposilite.kotlin.website/snapshots")
maven("https://www.jetbrains.com/intellij-repository/releases") {
    content { includeGroup("com.jetbrains.intellij.platform") }
}
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// build.gradle.kts of the module with the renderers
plugins {
    id("io.github.youndie.kompot.studio") version "0.36.2.125"
}

kompotStudio {
    target = "jvm"
    compilation = "test" // where the frame, the recordings and the goldens are
}
""") } }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-it-looks") }) {
        org.jetbrains.compose.web.dom.Text("How it looks")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// registered in META-INF/services/io.github.youndie.kompot.studio.KompotStudioConfigProvider
class StudioProvider : KompotStudioConfigProvider {
    override val title get() = "kompot studio — my app"

    override fun studioConfig() = KompotStudioConfig(
        registry = myRegistry(),
        frame = { brand, dark, content -> MyBrandFrame(brand, dark) { content() } },
        brands = listOf("brand-a", "brand-b"),
        schemas = mySpec.schemas() + (KompotProtocol.PROFILE_FILE_NAME to mySpec.profile()),
        sources = listOf(ScreenSource.Directory(recordingsDir, name = "recorded")),
        samples = showcaseComponents().map { wireTypeOf(it) to it },
    )
}
""") } }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-bash") }) { org.jetbrains.compose.web.dom.Text("""./gradlew :client:kompotStudio
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A brand is a string here on purpose: the toolkit knows nothing brand-specific, so the consumer hands over the list of names, a frame that understands them, and a rule for naming goldens. Another consumer's \"brands\" are regions or tenants, and the studio does not care.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-body-is-the-source-of-truth") }) {
        org.jetbrains.compose.web.dom.Text("The body is the source of truth")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There is one state, and it is the text. The tree, the palette, the inspector and the preview all read the same JSON and all write back into it: moving a node in the tree is a splice in the text, a property changed in the inspector is a property changed in the text, and the editor's caret lands on the node the tree selected. Nothing is kept beside the body that would have to be kept in agreement with it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The preview follows with a short debounce, and a body that no longer parses does not take the picture down: the editor tints the failing line and names the offset, while the tree and the preview keep showing the last body that parsed. A body that parses but cannot be decoded by the client — a string where a number belongs — becomes a finding on the render layer rather than a dead window.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("findings-by-layer") }) {
        org.jetbrains.compose.web.dom.Text("Findings, by layer")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A finding names the node it is about in the validator's path notation, and the tree carries the same notation, so clicking a finding selects the node without either side parsing the other. Errors come first: the body would not decode, or the profile forbids it. Then warnings: a component the build cannot draw and degraded to a placeholder, a token nobody defined. Then drafts — a node just added from the palette with its required fields still empty, grouped into one line per node rather than one per field.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The render layer is the one none of the others can replace. The client reports every component it could not draw and every action it could not act on through kompot's degradation sink, and the studio folds those reports into the same list, with the same shape, as the schema's.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Desktop only, and on a JetBrains Runtime 25: the window is drawn with Jewel, whose decorated window refuses any other JVM. The plugin provisions the runtime through a toolchain; a jar launched by hand on another JVM opens undecorated and says so.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The trackpad pinch on macOS reaches the window through Apple's gesture API in a package ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("java.desktop")
            }
            org.jetbrains.compose.web.dom.Text(" does not export; the plugin passes ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("--add-exports")
            }
            org.jetbrains.compose.web.dom.Text(" for it, a hand launch must.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Compose's accessibility is switched off in the studio by default. Compose Desktop 1.11 takes the window down when a focused node is removed under an assistive client, and an editor removes nodes all day; ")
            org.jetbrains.compose.web.dom.Code {
                org.jetbrains.compose.web.dom.Text("-Dcompose.accessibility.enable=true")
            }
            org.jetbrains.compose.web.dom.Text(" turns it back on.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("A golden is one file per screen, brand and theme, at 393×852. Capture and Compare take the frame at that size whatever the preview shows; there is no golden per device.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("The Kotlin DSL export is a draft with a marker on every name it had to guess. It is a starting point for the server side, not something to paste and ship.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Verified by hand on one consumer's build. The states a drag shows while it is in the air — the tinted target, the insertion line — were not: a synthetic gesture cannot be looked at mid-drag, so only the drop itself has been exercised.")
        }
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/kompot/tree/main/kompot-studio") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/kompot")
        }
    }
}
